/**
 * shipmentStatus - Catálogo das situações das remessas (Logística).
 *
 * Espelha `_shipment_transition_allowed` do banco apenas para exibir os botões
 * certos; a regra real é sempre revalidada no servidor (change_shipment_status).
 *
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial (Fase 2).
 */
export type ShipmentStatus =
  | 'draft' | 'preparing' | 'ready' | 'shipped' | 'in_transit'
  | 'delivered' | 'lost' | 'returned_to_sender' | 'cancelled';

/** Rótulo e tom (classe semântica do badge) por situação. */
export const SHIPMENT_STATUS_META: Record<ShipmentStatus, { label: string; tone: string }> = {
  draft: { label: 'Rascunho', tone: 'bg-muted text-muted-foreground' },
  preparing: { label: 'Em preparação', tone: 'bg-primary/10 text-primary' },
  ready: { label: 'Pronto p/ envio', tone: 'bg-primary/15 text-primary' },
  shipped: { label: 'Enviado', tone: 'bg-accent text-accent-foreground' },
  in_transit: { label: 'Em trânsito', tone: 'bg-accent text-accent-foreground' },
  delivered: { label: 'Entregue', tone: 'bg-secondary text-secondary-foreground' },
  lost: { label: 'Extraviado', tone: 'bg-destructive/10 text-destructive' },
  returned_to_sender: { label: 'Devolvido', tone: 'bg-destructive/10 text-destructive' },
  cancelled: { label: 'Cancelado', tone: 'bg-muted text-muted-foreground line-through' },
};

/** Ordem de exibição nos indicadores e filtros. */
export const SHIPMENT_STATUS_ORDER: ShipmentStatus[] = [
  'draft', 'preparing', 'ready', 'shipped', 'in_transit', 'delivered', 'lost', 'returned_to_sender', 'cancelled',
];

/** Próximas situações permitidas (mesmo mapa do servidor). */
export const SHIPMENT_TRANSITIONS: Record<ShipmentStatus, ShipmentStatus[]> = {
  draft: ['preparing', 'cancelled'],
  preparing: ['ready', 'draft', 'cancelled'],
  ready: ['shipped', 'preparing', 'cancelled'],
  shipped: ['in_transit', 'delivered', 'lost', 'returned_to_sender'],
  in_transit: ['delivered', 'lost', 'returned_to_sender'],
  delivered: [],
  lost: [],
  returned_to_sender: [],
  cancelled: [],
};

/** Situações finais: somente leitura (BR-LOG-005). */
export const isShipmentFinal = (s: ShipmentStatus) =>
  s === 'delivered' || s === 'cancelled' || s === 'lost' || s === 'returned_to_sender';

/** Rótulo da ação que leva a cada situação. */
export const SHIPMENT_ACTION_LABEL: Record<ShipmentStatus, string> = {
  draft: 'Voltar para rascunho',
  preparing: 'Iniciar preparação',
  ready: 'Marcar pronto p/ envio',
  shipped: 'Marcar enviado',
  in_transit: 'Em trânsito',
  delivered: 'Marcar entregue',
  lost: 'Extraviado',
  returned_to_sender: 'Devolvido ao remetente',
  cancelled: 'Cancelar remessa',
};

/**
 * Cor da situação na trilha (PhaseTrail): escala de azul que escurece conforme
 * avança; verde na entrega, vermelho em extravio/devolução e cinza no cancelamento.
 * RELEASE: 2026-10-16 v1.1.0 - Cores próprias para evitar fundo preto ilegível.
 */
export const SHIPMENT_STATUS_COLOR: Record<ShipmentStatus, string> = {
  draft: '#60A5FA',
  preparing: '#3B82F6',
  ready: '#2563EB',
  shipped: '#1D4ED8',
  in_transit: '#1E40AF',
  delivered: '#16A34A',
  lost: '#DC2626',
  returned_to_sender: '#DC2626',
  cancelled: '#6B7280',
};

/**
 * Destinatário pode ser trocado na edição só em remessa de envio ainda não postada
 * (antes de 'shipped'). Espelha `upsert_shipment` v1.2.0, que revalida no servidor.
 */
export const canEditShipmentRecipient = (direction: string, status: ShipmentStatus) =>
  direction === 'outbound' && (status === 'draft' || status === 'preparing' || status === 'ready');

/**
 * Progresso da remessa no caminho principal (Rascunho → Entregue).
 * Estados finais fora do caminho (extravio, devolução, cancelamento) contam como barra cheia.
 * RELEASE: 2026-10-16 v1.2.0 - Coluna Progresso da listagem.
 */
export const SHIPMENT_PROGRESS_PATH: ShipmentStatus[] = ['draft', 'preparing', 'ready', 'shipped', 'in_transit', 'delivered'];
export function shipmentProgress(status: ShipmentStatus): { step: number; total: number } {
  const total = SHIPMENT_PROGRESS_PATH.length;
  const i = SHIPMENT_PROGRESS_PATH.indexOf(status);
  return { step: i < 0 ? total : i + 1, total };
}

/** Allowlist de ordenação da listagem (espelha get_shipments_paginated v1.2.0). */
export const SHIPMENT_SORT_FIELDS = ['number', 'status', 'recipient', 'carrier', 'freight', 'expected', 'shipped_at', 'delivered_at', 'created_at'] as const;
export type ShipmentSortField = typeof SHIPMENT_SORT_FIELDS[number];

/**
 * Rótulo da ação considerando a origem: de "Pronto p/ envio" para "Em preparação"
 * é um retorno, não um início. RELEASE: 2026-10-16 v1.3.0.
 */
export function shipmentActionLabel(from: ShipmentStatus, to: ShipmentStatus): string {
  if (from === 'ready' && to === 'preparing') return 'Voltar para preparação';
  return SHIPMENT_ACTION_LABEL[to];
}

/**
 * Separa as ações do cabeçalho: principais (avanço no caminho) ficam visíveis;
 * retrocessos e desfechos (extravio, devolução, cancelamento) vão para o menu "...".
 * RELEASE: 2026-10-16 v1.4.0.
 */
const FORWARD_PATH: ShipmentStatus[] = ['preparing', 'ready', 'shipped', 'in_transit', 'delivered'];
export function splitShipmentActions(from: ShipmentStatus, next: ShipmentStatus[]) {
  const isBack = (t: ShipmentStatus) => t === 'draft' || (from === 'ready' && t === 'preparing');
  const primary = next.filter((t) => FORWARD_PATH.includes(t) && !isBack(t));
  const secondary = next.filter((t) => !primary.includes(t));
  return { primary, secondary };
}

/** Total de remessas para o card "Total Remessa": todas, exceto canceladas. RELEASE: 2026-10-16 v1.5.0. */
export function totalActiveShipments(counts: Partial<Record<ShipmentStatus, number>>): number {
  return SHIPMENT_STATUS_ORDER.filter((s) => s !== 'cancelled').reduce((n, s) => n + (counts[s] ?? 0), 0);
}
